/** @jsxImportSource @emotion/react */
import { SerializedStyles } from '@emotion/react';
import { useState } from 'react';

import { isScanHandoffable } from '@kaiizen/kai-pro-api';
import { useAuth, useFeatureFlags, useScanEditingContextSlice } from '@kaiizen/kai-pro-api-react-state';

import { useScopeApprovalAction } from '@/Client/State/useScopeApprovalAction';
import { ConfirmActionDialog } from '@/Client/Widgets/ConfirmActionDialog';
import { MAIN_NAVIGATION_BUTTON_STYLES } from './mainNavigationStyles';

export const BUTTON_TEST = 'Approve';
export const CONFIRM_ACTION_MESSAGE = 'Complete review and approve this scope?';
export const CONFIRM_ACTION_LABEL = 'Yes, Approve';
export const NO_APPROVE_PERMISSION_TITLE = 'You do not have permission to approve scope';

export const ApproveAndPublishHeaderAction = ({
  buttonStyle = MAIN_NAVIGATION_BUTTON_STYLES.primary,
  allowTurnBasedDraftApproval = false,
}: {
  buttonStyle?: SerializedStyles;
  allowTurnBasedDraftApproval?: boolean;
}) => {
  const { scopeApprovalStatus, approveAndPublish } = useScopeApprovalAction();
  const { userCan, userId } = useAuth();
  const { featureFlags } = useFeatureFlags();
  const { haveApprovedScan, project, scan, scanIsChangeOrder } = useScanEditingContextSlice([
    'haveApprovedScan',
    'project',
    'scan',
    'scanIsChangeOrder',
  ]);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // In the turn-based flow, `approved` can remain in the lifecycle chrome so a
  // user can still see the completed scope. It is not an invitation to approve
  // it again: after final approval, the server treats another update as a
  // no-op and the CTA must not linger as though there were another action.
  const turnBasedProject = featureFlags.turnBasedHandoff && !!project;
  if (turnBasedProject && (scan?.scanLifecycleStatus === 'approved' || (haveApprovedScan && !scanIsChangeOrder))) return null;
  if (!scopeApprovalStatus || scopeApprovalStatus.totalTasksToReviewCount === 0) return null;
  const isTurnBasedDraftApproval = allowTurnBasedDraftApproval
    && featureFlags.turnBasedHandoff
    && !!scan
    && isScanHandoffable(scan);
  if (allowTurnBasedDraftApproval && !isTurnBasedDraftApproval) return null;

  const canApprove = turnBasedProject
    ? !!userId && project.managerIds.includes(userId)
    : userCan('approve', 'Scan');
  if (!canApprove && featureFlags.turnBasedHandoff && project) return null;

  const isDisabled = !canApprove || !scopeApprovalStatus.canApprove;

  const submitApproveAndPublish = async () => {
    setSubmitting(true);
    try {
      await approveAndPublish();
      setModalOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type={'button'}
        css={buttonStyle}
        onClick={() => setModalOpen(true)}
        disabled={isDisabled}
        title={canApprove ? undefined : NO_APPROVE_PERMISSION_TITLE}
      >
        {BUTTON_TEST}
      </button>
      {modalOpen
        ? (
          <ConfirmActionDialog
            title={BUTTON_TEST}
            message={CONFIRM_ACTION_MESSAGE}
            confirmLabel={CONFIRM_ACTION_LABEL}
            busy={submitting}
            onConfirm={submitApproveAndPublish}
            onCancel={() => setModalOpen(false)}
          />
        )
        : null}
    </>
  );
};
